import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput,
  formatLatexPreview
} from "../../../../components/inputs/FractionInput";
import { formatMixedFractionLatex } from "../../../../utils/fractionLatex";

// ===== ARYTMETYKA UŁAMKÓW (dokładna) =====

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    [x, y] = [y, x % y];
  }
  return x || 1;
}

function simplify(num, den) {
  if (den === 0) return { num: 0, den: 1 };
  const sign = (num < 0) !== (den < 0) ? -1 : 1;
  const a = Math.abs(num);
  const b = Math.abs(den);
  const g = gcd(a, b);
  return { num: sign * (a / g), den: b / g };
}

// Formatowanie dokładnego ułamka licznik/mianownik -> liczba całkowita lub ułamek mieszany
const formatFractionLatex = formatMixedFractionLatex;

// ===== GENERATOR =====

function buildSlopeOptions() {
  const properFractions = [
    { num: 1, den: 4 },
    { num: 1, den: 3 },
    { num: 1, den: 2 },
    { num: 2, den: 3 },
    { num: 3, den: 4 },
  ];
  const options = [];
  for (let whole = 0; whole <= 4; whole += 1) {
    properFractions.forEach(({ num, den }) => {
      const value = whole * den + num;
      options.push({ num: value, den });
    });
  }
  options.push({ num: 5, den: 1 });

  // dodaj warianty ujemne
  return options.flatMap(({ num, den }) => [
    { num, den },
    { num: -num, den },
  ]);
}

const SLOPE_OPTIONS = buildSlopeOptions();

export function generateTask() {
  const slope = SLOPE_OPTIONS[Math.floor(Math.random() * SLOPE_OPTIONS.length)];
  const x = Math.floor(Math.random() * 19 - 9); // -9 do 9
  const y = Math.floor(Math.random() * 19 - 9); // -9 do 9

  // b = y - a*x  (dokładnie, jako ułamek)
  const intercept = simplify(y * slope.den - slope.num * x, slope.den);

  return {
    type: "FindYInterceptTask",
    data: {
      aNum: slope.num,
      aDen: slope.den,
      x,
      y,
      bNum: intercept.num,
      bDen: intercept.den,
      a: slope.num / slope.den,
      b: intercept.num / intercept.den,
    },
  };
}

// ===== RENDERING =====

export function renderTask({ data }) {
  const { aNum, aDen, x, y } = data;

  return (
    <p>
      Prosta o współczynniku kierunkowym <InlineMath math={`a = ${formatFractionLatex(aNum, aDen)}`} /> przechodzi przez punkt <InlineMath math={`A = (${x}, ${y})`} />.{" "}
      Oblicz wyraz wolny <InlineMath math="b" /> równania kierunkowego.
    </p>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="b"
    />
  );
}

// ===== CHECK =====

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const given = userAnswer.parsed;
  const expected = data.b;
  return given !== null && Number.isFinite(given) && Math.abs(given - expected) < 0.01;
}

// ===== ODPOWIEDŹ =====

export function renderAnswer({ data }) {
  const { aNum, aDen, bNum, bDen, b } = data;
  const aLatex = formatFractionLatex(aNum, aDen);
  const bLatex = formatFractionLatex(bNum, bDen);

  let equation;
  if (b === 0) {
    equation = `\\displaystyle y = ${aLatex}x`;
  } else {
    const bSign = b < 0 ? "-" : "+";
    const bAbsLatex = formatFractionLatex(Math.abs(bNum), bDen);
    equation = `\\displaystyle y = ${aLatex}x ${bSign} ${bAbsLatex}`;
  }

  return (
    <div className="space-y-4">
      <div style={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}>
        <InlineMath math={`b = ${bLatex}`} />
      </div>
      <div>
        <strong>Równanie prostej ma postać:</strong>
        <div style={{ display: "block", textAlign: "left", marginTop: "0.5rem" }}>
          <InlineMath math={equation} />
        </div>
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const { aNum, aDen, x, y, bNum, bDen } = data;

  const aLatex = formatFractionLatex(aNum, aDen);
  const bLatex = formatFractionLatex(bNum, bDen);

  // a*x dokładnie
  const ax = simplify(aNum * x, aDen);
  const axLatex = formatFractionLatex(ax.num, ax.den);

  // nawiasy tylko dla wartości ujemnych
  const aTerm = aNum < 0 ? `(${aLatex})` : aLatex;
  const axTerm = ax.num < 0 ? `(${axLatex})` : axLatex;

  return (
    <div className="space-y-2">
      <p><strong>Krok 1:</strong> Korzystamy ze wzoru na równanie prostej w postaci kierunkowej:</p>
      <BlockMath math="y = ax + b" />

      <p><strong>Krok 2:</strong> Przekształcamy wzór, aby obliczyć <InlineMath math="b" />:</p>
      <BlockMath math="b = y - ax" />

      <p><strong>Krok 3:</strong> Podstawiamy dane i obliczamy:</p>
      <BlockMath math={`b = ${y} - ${aTerm} \\cdot ${x}`} />
      <BlockMath math={`b = ${y} - ${axTerm}`} />
      <BlockMath math={`b = ${bLatex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(userAnswer.raw).replace(/^a/, "b")} />;
}
